import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts'
import { cn } from '@/lib/utils'
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from '@/components/ui/chart'

/**
 * Figma (node 517:111675 — Area Chart):
 *
 * Chart colors: --chart-1 (#ea580c), --chart-2 (teal) via CSS variables
 * Grid: horizontal dashed lines
 * X axis: text-xs text-muted-foreground, tick line hidden
 * Y axis: text-xs text-muted-foreground (optional, hidden by default)
 * Curve types: natural (default), linear, step
 * Fill opacity: 0.4 for area
 * Gradient: linearGradient from color/0.8 to color/0.1
 * Legend: 8px rounded-xs colored dot + text-xs label
 *
 * Playground variants:
 *   Basic, Linear, Step, Stacked, Stacked Expanded,
 *   Legend, Icons, Gradient, Axes
 */

export type AreaChartCurveType = 'natural' | 'linear' | 'step'

export interface IntuiAreaChartProps {
  /** Chart data array */
  data: Record<string, unknown>[]
  /** Chart configuration (keys, labels, colors) */
  config: ChartConfig
  /** Data keys to render as areas (defaults to all config keys) */
  dataKeys?: string[]
  /** X axis data key */
  xAxisKey?: string
  /** Curve interpolation type */
  curveType?: AreaChartCurveType
  /** Stack areas on top of each other */
  stacked?: boolean
  /** Normalize stacked areas to 100% */
  stackedExpanded?: boolean
  /** Apply gradient fill instead of solid */
  gradient?: boolean
  /** Show chart legend */
  showLegend?: boolean
  /** Show X axis */
  showXAxis?: boolean
  /** Show Y axis */
  showYAxis?: boolean
  /** Show cartesian grid */
  showGrid?: boolean
  /** Show tooltip on hover */
  showTooltip?: boolean
  /** Fill opacity for areas */
  fillOpacity?: number
  /** Additional className for the container */
  className?: string
}

function IntuiAreaChart({
  data,
  config,
  dataKeys,
  xAxisKey = 'month',
  curveType = 'natural',
  stacked = false,
  stackedExpanded = false,
  gradient = false,
  showLegend = false,
  showXAxis = true,
  showYAxis = false,
  showGrid = true,
  showTooltip = true,
  fillOpacity = 0.4,
  className,
}: IntuiAreaChartProps) {
  const keys = dataKeys ?? Object.keys(config)
  const stackId = stacked || stackedExpanded ? 'stack' : undefined

  return (
    <ChartContainer config={config} className={cn(className)}>
      <AreaChart
        data={data}
        stackOffset={stackedExpanded ? 'expand' : undefined}
        margin={{ left: showYAxis ? 12 : 0, right: 12 }}
      >
        {showGrid && <CartesianGrid vertical={false} />}
        {showXAxis && (
          <XAxis
            dataKey={xAxisKey}
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tickFormatter={(value) =>
              typeof value === 'string' ? value.slice(0, 3) : value
            }
          />
        )}
        {showYAxis && (
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tickFormatter={
              stackedExpanded
                ? (value) => `${(value * 100).toFixed(0)}%`
                : undefined
            }
          />
        )}
        {showTooltip && (
          <ChartTooltip
            cursor={false}
            content={<ChartTooltipContent indicator="dot" />}
          />
        )}
        {gradient && keys.map((key) => (
          <defs key={`gradient-${key}`}>
            <linearGradient id={`fill-${key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={`var(--color-${key})`} stopOpacity={0.8} />
              <stop offset="95%" stopColor={`var(--color-${key})`} stopOpacity={0.1} />
            </linearGradient>
          </defs>
        ))}
        {keys.map((key) => (
          <Area
            key={key}
            dataKey={key}
            type={curveType}
            fill={gradient ? `url(#fill-${key})` : `var(--color-${key})`}
            fillOpacity={gradient ? 1 : fillOpacity}
            stroke={`var(--color-${key})`}
            stackId={stackId}
          />
        ))}
        {showLegend && (
          <ChartLegend content={<ChartLegendContent payload={[]} />} />
        )}
      </AreaChart>
    </ChartContainer>
  )
}
IntuiAreaChart.displayName = 'IntuiAreaChart'

export { IntuiAreaChart }
export type { ChartConfig }
